// ─────────────────────────────────────────────────────────────────────────────
// CoreConnectionCard — one honest view of the page-to-core connection.
//
// Identity and route come only from the page origin. Live connection state comes
// only from the existing app-wide Socket.IO singleton. Unknown observations stay
// UNKNOWN: this card never substitutes build mode, a public hostname, or a guessed
// route. It owns only the listeners below and never mutates/disconnects the socket.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { getRexySocket } from '../hooks/useRexySocket';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';

const UNKNOWN = 'UNKNOWN';
const CORE_MAGICDNS_HOST = 'edgexpert-8cb2.tail2c5299.ts.net';

interface PageConnection {
  core: string;
  endpoint: string;
  path: string;
}

function observePageConnection(): PageConnection {
  const { hostname, origin, protocol } = window.location;
  const normalizedHost = hostname.toLowerCase();
  const isTailnetHttps =
    protocol === 'https:' && normalizedHost === CORE_MAGICDNS_HOST;
  const isLoopback =
    normalizedHost === 'localhost' ||
    normalizedHost === '127.0.0.1' ||
    normalizedHost === '::1';

  return {
    core: isTailnetHttps ? hostname : UNKNOWN,
    endpoint: origin || UNKNOWN,
    path: isTailnetHttps
      ? 'PRIVATE TAILNET HTTPS'
      : isLoopback
      ? 'LOOPBACK DEVELOPMENT'
      : UNKNOWN,
  };
}

function formatTransport(name: string | null): string {
  if (name === 'websocket') return 'WebSocket';
  if (name === 'polling') return 'polling';
  return name ?? UNKNOWN;
}

function formatHeartbeat(timestamp: number | null): string {
  if (timestamp == null) return UNKNOWN;
  return new Date(timestamp).toLocaleTimeString('en-GB', { hour12: false });
}

function StatRow({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div style={styles.row}>
      <span style={styles.label}>{label}</span>
      <span style={{ ...styles.value, color: color ?? REXY_CONSOLE.primary }}>{value}</span>
    </div>
  );
}

export interface CoreConnectionCardProps {
  /**
   * 'detail' — the full stat block (CORE, ENDPOINT, PATH, TRANSPORT, RECONNECTS,
   *            LAST HEARTBEAT). Lives on the SETTINGS page.
   * 'line'   — one honest line: ONLINE / OFFLINE / UNKNOWN. Lives on the Console.
   *
   * Same card, same listeners, same observations — only how much of it is drawn.
   */
  variant?: 'detail' | 'line';
}

export default function CoreConnectionCard({ variant = 'detail' }: CoreConnectionCardProps) {
  const [{ core, endpoint, path }] = useState(observePageConnection);
  // THREE-STATE, HONEST-NULL. `null` is "not observed yet" and reads UNKNOWN — it is
  // not offline. The old boolean started false, so the card asserted OFFLINE for the
  // moment before its own effect had looked. An unobserved link is unknown.
  const [connected, setConnected] = useState<boolean | null>(null);
  const [transport, setTransport] = useState<string | null>(null);
  const [reconnects, setReconnects] = useState(0);
  const [lastHeartbeat, setLastHeartbeat] = useState<number | null>(null);

  useEffect(() => {
    const socket = getRexySocket();
    const manager = socket.io;
    let engine: any = null;

    const syncTransport = () => {
      const currentEngine: any = manager.engine;
      setTransport(currentEngine?.transport?.name ?? null);
    };
    const onHeartbeat = () => setLastHeartbeat(Date.now());

    const unbindEngine = () => {
      if (!engine) return;
      engine.off('ping', onHeartbeat);
      engine.off('pong', onHeartbeat);
      engine.off('upgrade', syncTransport);
      engine = null;
    };
    const bindEngine = () => {
      engine = manager.engine as any;
      if (!engine) return;
      syncTransport();
      engine.on('ping', onHeartbeat);
      engine.on('pong', onHeartbeat);
      engine.on('upgrade', syncTransport);
    };

    const onConnect = () => {
      setConnected(true);
      unbindEngine();
      bindEngine();
    };
    const onDisconnect = () => {
      setConnected(false);
      setTransport(null);
      unbindEngine();
    };
    const onReconnect = () => setReconnects((count) => count + 1);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    manager.on('reconnect', onReconnect);

    // NOT-YET-OBSERVED IS NOT OFFLINE (owner Item 3 — the honest-null rail).
    //
    // This used to be `setConnected(socket.connected)`, which collapsed two different
    // things into one: socket.connected is false BOTH when the socket has been
    // observed to drop AND when it simply has not connected yet. So a socket that was
    // still coming up — or one that is unreachable and has produced no connect or
    // disconnect at all — rendered OFFLINE, which is the surface asserting something
    // it has not verified. Same defect class as the daemon_ctl light reading green
    // because a socket accepted.
    //
    // A live socket at mount IS a real observation, so it is taken. A false one is
    // not an observation of anything, so the state stays null and reads UNKNOWN until
    // an actual 'connect' or 'disconnect' arrives. OFFLINE now means observed-offline.
    if (socket.connected) {
      setConnected(true);
      bindEngine();
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      manager.off('reconnect', onReconnect);
      unbindEngine();
    };
  }, []);

  const linkLabel = connected == null ? 'unknown' : connected ? 'online' : 'offline';
  const linkColor =
    connected == null
      ? REXY_CONSOLE.dimText
      : connected
      ? REXY_CONSOLE.primary
      : REXY_CONSOLE.critical;

  if (variant === 'line') {
    // THE CONSOLE KEEPS ONE LINE. The detail moved to SETTINGS; nothing was deleted,
    // and the listeners above are identical in both variants.
    return (
      <div style={styles.card}>
        <div style={styles.row}>
          <span style={styles.label}>║ CORE CONNECTION</span>
          <span style={{ ...styles.value, color: linkColor }}>{linkLabel.toUpperCase()}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.sectionHeader}>║ CORE CONNECTION</div>
      <StatRow label="CORE" value={core} />
      <StatRow label="LINK" value={linkLabel} color={linkColor} />
      <StatRow label="ENDPOINT" value={endpoint} />
      <StatRow label="PATH" value={path} />
      <StatRow label="TRANSPORT" value={formatTransport(transport)} />
      <StatRow label="RECONNECTS" value={String(reconnects)} />
      <StatRow
        label="LAST HEARTBEAT"
        value={formatHeartbeat(lastHeartbeat)}
        color={lastHeartbeat == null ? REXY_CONSOLE.dimText : REXY_CONSOLE.primary}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
    minWidth: 0,
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: REXY_FONT,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
    marginBottom: 10,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 10,
    padding: '3px 0',
  },
  label: {
    flexShrink: 0,
    fontFamily: REXY_FONT,
    fontSize: 11,
    letterSpacing: 1.5,
    color: REXY_CONSOLE.primary,
  },
  value: {
    minWidth: 0,
    fontFamily: REXY_FONT,
    fontSize: 11,
    textAlign: 'right',
    overflowWrap: 'anywhere',
  },
};
